// src/pages/SmartAlertsDashboard.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FaBell,
  FaBolt,
  FaCalendarAlt,
  FaCheckCircle,
  FaEnvelope,
  FaExclamationTriangle,
  FaGift,
  FaPause,
  FaPlay,
  FaPlus,
  FaPlane,
  FaSearch,
  FaSync,
  FaTrash,
} from 'react-icons/fa';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { deleteSmartAlert, listSmartAlerts, toggleSmartAlert, type SmartAlert } from '../lib/smartAlerts';

const copy = {
  fr: {
    badge: 'Tableau de bord alertes',
    title: 'Mes alertes intelligentes',
    subtitle: 'Gère les radars Forfeo qui surveillent les offres, expériences, cadeaux et régions pour toi.',
    newAlert: 'Nouvelle alerte',
    loading: 'Chargement des alertes...',
    emptyTitle: 'Aucune alerte pour le moment',
    emptyText: 'Crée ton premier radar Forfeo pour recevoir les occasions pertinentes au bon moment.',
    enterEmail: 'Entre ton courriel pour retrouver tes alertes anonymes',
    search: 'Rechercher',
    active: 'Active',
    paused: 'En pause',
    archived: 'Archivée',
    pause: 'Pause',
    reactivate: 'Réactiver',
    delete: 'Supprimer',
    confirmDelete: 'Supprimer cette alerte définitivement ?',
    budget: 'Budget',
    region: 'Région',
    frequency: 'Fréquence',
    created: 'Créée le',
    lastSent: 'Dernier envoi',
    never: 'Jamais',
    loadError: 'Impossible de charger les alertes.',
  },
  en: {
    badge: 'Alerts dashboard',
    title: 'My smart alerts',
    subtitle: 'Manage the Forfeo radars watching offers, experiences, gifts and regions for you.',
    newAlert: 'New alert',
    loading: 'Loading alerts...',
    emptyTitle: 'No alerts yet',
    emptyText: 'Create your first Forfeo radar and receive relevant opportunities at the right time.',
    enterEmail: 'Enter your email to find anonymous alerts',
    search: 'Search',
    active: 'Active',
    paused: 'Paused',
    archived: 'Archived',
    pause: 'Pause',
    reactivate: 'Reactivate',
    delete: 'Delete',
    confirmDelete: 'Permanently delete this alert?',
    budget: 'Budget',
    region: 'Region',
    frequency: 'Frequency',
    created: 'Created on',
    lastSent: 'Last sent',
    never: 'Never',
    loadError: 'Unable to load alerts.',
  },
};

const typeLabels: Record<string, { fr: string; en: string }> = {
  last_call: { fr: 'Last Call', en: 'Last Call' },
  restaurant: { fr: 'Restaurants', en: 'Restaurants' },
  hotel: { fr: 'Hôtels', en: 'Hotels' },
  gift: { fr: 'Idées cadeaux', en: 'Gift ideas' },
  family: { fr: 'Famille', en: 'Family' },
  region: { fr: 'Région', en: 'Region' },
  evasion: { fr: 'Évasion', en: 'Escape' },
  flight: { fr: 'Vols / billets d’avion', en: 'Flights / airfare' },
  corporate: { fr: 'Corporatif', en: 'Corporate' },
};

const statusStyle: Record<string, string> = {
  active: 'bg-emerald-50 text-emerald-700 border-emerald-100',
  paused: 'bg-amber-50 text-amber-700 border-amber-100',
  archived: 'bg-slate-100 text-slate-500 border-slate-200',
};

const formatAlertMoney = (value: unknown) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return '—';
  return new Intl.NumberFormat('fr-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 }).format(n);
};

const getAlertBudget = (alert: SmartAlert) => {
  const meta = (alert.metadata || {}) as any;
  return alert.max_budget ?? meta.max_budget ?? meta.budget ?? meta.flight?.max_budget ?? meta.flight?.budget ?? null;
};

export default function SmartAlertsDashboard() {
  const { i18n } = useTranslation();
  const lang = i18n.language?.startsWith('en') ? 'en' : 'fr';
  const text = copy[lang];
  const [alerts, setAlerts] = useState<SmartAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [filter, setFilter] = useState<'all' | 'active' | 'paused'>('all');

  const fetchAlerts = async (lookupEmail?: string) => {
    setLoading(true);
    setError(null);
    const response = await listSmartAlerts(lookupEmail || undefined);
    setLoading(false);

    if (!response.ok) {
      setError(response.details || response.error || text.loadError);
      setAlerts([]);
      return;
    }

    setAlerts(response.alerts || []);
  };

  useEffect(() => {
    fetchAlerts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredAlerts = useMemo(() => {
    return alerts.filter((alert) => filter === 'all' ? alert.status !== 'archived' : alert.status === filter);
  }, [alerts, filter]);

  const handleToggle = async (alert: SmartAlert) => {
    const nextStatus = alert.status === 'active' ? 'paused' : 'active';
    setActionLoading(alert.id);
    const response = await toggleSmartAlert(alert.id, nextStatus);
    setActionLoading(null);
    if (!response.ok) return setError(response.details || response.error || text.loadError);
    setAlerts((prev) => prev.map((item) => item.id === alert.id ? { ...item, status: nextStatus } : item));
  };

  const handleDelete = async (alert: SmartAlert) => {
    if (!window.confirm(text.confirmDelete)) return;
    setActionLoading(alert.id);
    const response = await deleteSmartAlert(alert.id);
    setActionLoading(null);
    if (!response.ok) return setError(response.details || response.error || text.loadError);
    setAlerts((prev) => prev.filter((item) => item.id !== alert.id));
  };

  const formatDate = (value?: string | null) => {
    if (!value) return text.never;
    return new Date(value).toLocaleDateString(lang === 'en' ? 'en-CA' : 'fr-CA', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-950 font-sans">
      <Header />
      <main className="pt-24 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-cyan-100 px-4 py-2 text-xs font-black uppercase tracking-widest text-cyan-700"><FaBell /> {text.badge}</div>
              <h1 className="mt-5 text-4xl md:text-6xl font-black tracking-tight">{text.title}</h1>
              <p className="mt-3 max-w-3xl text-lg leading-8 text-slate-600">{text.subtitle}</p>
            </div>
            <Link to="/alertes-intelligentes" className="inline-flex items-center justify-center gap-3 rounded-2xl bg-slate-950 px-7 py-4 text-sm font-black uppercase tracking-widest text-white shadow-xl transition hover:-translate-y-0.5">
              <FaPlus /> {text.newAlert}
            </Link>
          </div>

          <div className="mb-8 grid lg:grid-cols-12 gap-4">
            <div className="lg:col-span-7 rounded-3xl border border-slate-100 bg-white p-4 shadow-sm">
              <form onSubmit={(e) => { e.preventDefault(); fetchAlerts(email); }} className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1"><FaEnvelope className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" /><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={text.enterEmail} className="w-full rounded-2xl border border-slate-200 py-4 pl-11 pr-4 font-bold outline-none focus:border-cyan-400 focus:ring-4 focus:ring-cyan-100" /></div>
                <button className="rounded-2xl bg-cyan-600 px-6 py-4 text-sm font-black uppercase tracking-widest text-white"><FaSearch className="inline mr-2" />{text.search}</button>
              </form>
            </div>
            <div className="lg:col-span-5 grid grid-cols-3 gap-2 rounded-3xl border border-slate-100 bg-white p-2 shadow-sm">
              {(['all', 'active', 'paused'] as const).map((item) => (
                <button key={item} onClick={() => setFilter(item)} className={`rounded-2xl px-4 py-3 text-xs font-black uppercase tracking-widest transition ${filter === item ? 'bg-slate-950 text-white' : 'text-slate-500 hover:bg-slate-50'}`}>
                  {item === 'all' ? 'Toutes' : item === 'active' ? text.active : text.paused}
                </button>
              ))}
            </div>
          </div>

          {error && <div className="mb-8 rounded-2xl border border-red-100 bg-red-50 p-4 text-sm font-bold text-red-700 flex items-center gap-3"><FaExclamationTriangle /> {error}</div>}

          {loading ? (
            <div className="py-20 text-center"><FaSync className="mx-auto mb-4 animate-spin text-4xl text-cyan-500" /><p className="font-bold text-slate-500">{text.loading}</p></div>
          ) : filteredAlerts.length === 0 ? (
            <div className="rounded-[2.5rem] border border-slate-100 bg-white p-10 md:p-16 text-center shadow-xl shadow-slate-900/5">
              <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-cyan-50 text-cyan-600 text-4xl"><FaBell /></div>
              <h2 className="mt-6 text-3xl font-black">{text.emptyTitle}</h2>
              <p className="mx-auto mt-3 max-w-xl text-slate-500">{text.emptyText}</p>
              <Link to="/alertes-intelligentes" className="mt-8 inline-flex items-center gap-3 rounded-2xl bg-slate-950 px-7 py-4 text-sm font-black uppercase tracking-widest text-white"><FaPlus /> {text.newAlert}</Link>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
              {filteredAlerts.map((alert) => {
                const label = typeLabels[alert.alert_type || 'last_call'] || typeLabels.last_call;
                const style = statusStyle[alert.status] || statusStyle.archived;
                const displayedBudget = formatAlertMoney(getAlertBudget(alert));
                return (
                  <article key={alert.id} className="rounded-[2rem] border border-slate-100 bg-white p-6 shadow-xl shadow-slate-900/5 transition hover:-translate-y-1 hover:shadow-2xl">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-cyan-50 text-cyan-600 text-xl">
                          {alert.alert_type === 'gift' ? <FaGift /> : alert.alert_type === 'last_call' ? <FaBolt /> : alert.alert_type === 'flight' ? <FaPlane /> : <FaBell />}
                        </div>
                        <div>
                          <h3 className="font-black text-lg leading-tight">{lang === 'en' ? label.en : label.fr}</h3>
                          <p className="text-xs text-slate-400 font-bold">{text.created} {formatDate(alert.created_at)}</p>
                        </div>
                      </div>
                      <span className={`rounded-full border px-3 py-1 text-[10px] font-black uppercase tracking-widest ${style}`}>{alert.status === 'active' ? text.active : alert.status === 'paused' ? text.paused : text.archived}</span>
                    </div>

                    <div className="mt-6 grid grid-cols-2 gap-3">
                      <div className="rounded-2xl bg-slate-50 p-4"><div className="text-[10px] font-black uppercase tracking-widest text-slate-400">{alert.alert_type === 'flight' ? (lang === 'en' ? 'Route' : 'Vol') : text.region}</div><div className="mt-1 font-black">{alert.alert_type === 'flight' ? `${(alert.metadata as any)?.flight?.origin || '—'} → ${(alert.metadata as any)?.flight?.destination || '—'}` : (alert.city || alert.region || 'Québec')}</div></div>
                      <div className="rounded-2xl bg-slate-50 p-4"><div className="text-[10px] font-black uppercase tracking-widest text-slate-400">{text.budget}</div><div className="mt-1 font-black">{displayedBudget}</div></div>
                      <div className="rounded-2xl bg-slate-50 p-4"><div className="text-[10px] font-black uppercase tracking-widest text-slate-400">{text.frequency}</div><div className="mt-1 font-black">{alert.frequency || 'instant'}</div></div>
                      <div className="rounded-2xl bg-slate-50 p-4"><div className="text-[10px] font-black uppercase tracking-widest text-slate-400">{text.lastSent}</div><div className="mt-1 font-black">{formatDate(alert.last_sent_at)}</div></div>
                    </div>

                    {alert.keywords && <p className="mt-5 rounded-2xl bg-cyan-50 p-4 text-sm font-bold text-cyan-900">“{alert.keywords}”</p>}

                    <div className="mt-6 flex gap-2">
                      <button disabled={actionLoading === alert.id} onClick={() => handleToggle(alert)} className="flex-1 rounded-2xl border border-slate-200 px-4 py-3 text-xs font-black uppercase tracking-widest text-slate-700 hover:bg-slate-50 disabled:opacity-50">
                        {alert.status === 'active' ? <FaPause className="inline mr-2" /> : <FaPlay className="inline mr-2" />}{alert.status === 'active' ? text.pause : text.reactivate}
                      </button>
                      <button disabled={actionLoading === alert.id} onClick={() => handleDelete(alert)} className="rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-xs font-black uppercase tracking-widest text-red-700 hover:bg-red-100 disabled:opacity-50">
                        <FaTrash />
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
